import { isNotEmptyString } from '@racemap/utilities/functions/utils';
import { isValidRREventId } from '@racemap/utilities/functions/validation';
import { Form, Input, Space, Typography } from 'antd';
import { type FC, useEffect } from 'react';
import {
  ImportExportParticipantsLink,
  LeaderboardLink,
  VisualisationLink,
} from 'src/components/BasicComponents';
import { useCurrentEvent } from 'src/lib/customHooks/useCurrentEvent';
import { useEventFieldGenerator } from 'src/lib/customHooks/useEventFieldGenerator';
import { toast } from 'src/lib/toast';

const { Text, Title } = Typography;

export const RaceResultIntergrationConfigurator: FC = () => {
  const [form] = Form.useForm();
  const currentEvent = useCurrentEvent();
  const fieldGenerator = useEventFieldGenerator(currentEvent);
  if (fieldGenerator == null || currentEvent == null) return <></>;

  const { value, onChange } = fieldGenerator('integrations.raceresult.eventId');

  const handleBlur = async () => {
    try {
      const res = await form.validateFields();
      await onChange(res.eventId);
      toast.success(res.eventId ? 'Event ID saved' : 'Event ID cleared');
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    form.setFieldsValue({ eventId: value });
  }, [value]);

  return (
    <Space orientation="vertical" size="large">
      <Text>
        You can activate the integration by enter a valid RACE RESULT event id. Then you can use the
        intergration to sync your participants or use the RACE RESULT leaderboard or registration
        form.
      </Text>
      <Form onBlur={handleBlur} form={form} wrapperCol={{ span: 6 }} layout="vertical">
        <Form.Item
          label="Event ID"
          rules={[{ validator: fieldValidator }]}
          name="eventId"
          validateTrigger={false}
          style={{ marginBottom: 0 }}
        >
          <Input />
        </Form.Item>
      </Form>

      {isNotEmptyString(value) && (
        <Space orientation="vertical">
          <Title level={5}>Possible RACE RESULT Connections</Title>
          <Text>
            &raquo; Sync your participants in{' '}
            <ImportExportParticipantsLink eventId={currentEvent.id} />
          </Text>
          <Text>
            &raquo; Integrate your registration form in{' '}
            <VisualisationLink eventId={currentEvent.id} />
          </Text>
          <Text>
            &raquo; Embed you results in <LeaderboardLink eventId={currentEvent.id} />
          </Text>
        </Space>
      )}
    </Space>
  );
};

const fieldValidator = async (_rule: any, value: string) => {
  if (value === '') return;
  if (!isValidRREventId(value)) {
    throw new Error(
      'Please enter a valid event id! Only number, with a minimum length of 4 and a maximum length of 8 digits.',
    );
  }
};
